import userUtils from './userUtils';

// Global default prefix - can be changed
let globalPrefix = 'AIQ';

/**
 * Set the global default prefix for all log messages
 */
export function setGlobalPrefix(prefix: string): void {
	globalPrefix = prefix;
}

/**
 * Get the current global prefix
 */
export function getGlobalPrefix(): string {
	return globalPrefix;
}

export function log(...args: unknown[]) {
	if (isDebugEnabled()) {
		console.debug(...formatMessage(globalPrefix, '#00a67c', args));
	}
}

export function info(...args: unknown[]) {
	if (isDebugEnabled()) {
		console.debug('🔍', ...formatMessage(globalPrefix, '#007bff', args));
	}
}

export function warn(...args: unknown[]) {
	if (isDebugEnabled()) {
		console.debug('⚠️', ...formatMessage(globalPrefix, '#ffc107', args));
	}
}

export function errLog(...args: unknown[]) {
	if (isDebugEnabled()) {
		console.debug('❌', ...formatMessage(globalPrefix, '#dc3545', args));
	}
}

export function success(...args: unknown[]) {
	if (isDebugEnabled()) {
		console.debug('✅', ...formatMessage(globalPrefix, '#28a745', args));
	}
}

// Formatting options type
type FormatOptions = {
	color?: string;
	backgroundColor?: string;
	fontWeight?: 'normal' | 'bold';
	fontStyle?: 'normal' | 'italic';
	textDecoration?: 'none' | 'underline' | 'line-through';
	fontSize?: string;
};

type LogField = {
	name?: string;
	value: unknown;
	format?: FormatOptions;
	newLine?: boolean;
};

class LoggerChain {
	private message: string;
	private fields: LogField[] = [];
	private defaultColor: string;
	private icon?: string;
	private outputTimer?: NodeJS.Timeout;
	private grouped = false;
	private groupLabel?: string;
	private customPrefix?: string;

	constructor(message: string, defaultColor: string, icon?: string) {
		this.message = message;
		this.defaultColor = defaultColor;
		this.icon = icon;

		// Schedule auto-output
		this.scheduleOutput();
	}

	// Set a custom prefix for this log chain
	prefix(customPrefix: string): LoggerChain {
		// Cancel previous timer since we're modifying the chain
		if (this.outputTimer) {
			clearTimeout(this.outputTimer);
		}

		this.customPrefix = customPrefix;

		// Schedule new output
		this.scheduleOutput();
		return this;
	}

	add(nameOrValue: string | unknown, value?: unknown, formatOrColor?: string | FormatOptions): LoggerChain {
		// Cancel previous timer since we're adding more data
		if (this.outputTimer) {
			clearTimeout(this.outputTimer);
		}

		let format: FormatOptions | undefined;

		// Handle backward compatibility with color string
		if (typeof formatOrColor === 'string') {
			format = { color: formatOrColor };
		} else {
			format = formatOrColor;
		}

		if (typeof nameOrValue === 'string' && arguments.length > 1) {
			// Field with name: add('campaignID', '1234', format)
			this.fields.push({ name: nameOrValue, value, format, newLine: false });
		} else {
			// Just a value: add(response) or add(response, format)
			this.fields.push({
				value: nameOrValue,
				format: typeof value === 'string' ? { color: value } : format,
				newLine: false,
			});
		}

		// Schedule new output
		this.scheduleOutput();
		return this;
	}

	// Add field on a new line
	addnl(nameOrValue: string | unknown, value?: unknown, formatOrColor?: string | FormatOptions): LoggerChain {
		// Cancel previous timer since we're adding more data
		if (this.outputTimer) {
			clearTimeout(this.outputTimer);
		}

		let format: FormatOptions | undefined;

		// Handle backward compatibility with color string
		if (typeof formatOrColor === 'string') {
			format = { color: formatOrColor };
		} else {
			format = formatOrColor;
		}

		if (typeof nameOrValue === 'string' && arguments.length > 1) {
			// Field with name: addnl('campaignID', '1234', format)
			this.fields.push({ name: nameOrValue, value, format, newLine: true });
		} else {
			// Just a value: addnl(response) or addnl(response, format)
			this.fields.push({
				value: nameOrValue,
				format: typeof value === 'string' ? { color: value } : format,
				newLine: true,
			});
		}

		// Schedule new output
		this.scheduleOutput();
		return this;
	}

	// Add bold text
	bold(nameOrValue: string | unknown, value?: unknown, color?: string): LoggerChain {
		const format: FormatOptions = { fontWeight: 'bold', color };
		return this.add(nameOrValue, value, format);
	}

	// Add italic text
	italic(nameOrValue: string | unknown, value?: unknown, color?: string): LoggerChain {
		const format: FormatOptions = { fontStyle: 'italic', color };
		return this.add(nameOrValue, value, format);
	}

	// Add underlined text
	underline(nameOrValue: string | unknown, value?: unknown, color?: string): LoggerChain {
		const format: FormatOptions = { textDecoration: 'underline', color };
		return this.add(nameOrValue, value, format);
	}

	// Add text with background color
	bg(nameOrValue: string | unknown, value?: unknown, backgroundColor?: string, textColor?: string): LoggerChain {
		const format: FormatOptions = {
			backgroundColor,
			color: textColor,
			fontWeight: 'bold',
		};
		return this.add(nameOrValue, value, format);
	}

	// Add large text
	large(nameOrValue: string | unknown, value?: unknown, color?: string): LoggerChain {
		const format: FormatOptions = { fontSize: '16px', fontWeight: 'bold', color };
		return this.add(nameOrValue, value, format);
	}

	// Add small text
	small(nameOrValue: string | unknown, value?: unknown, color?: string): LoggerChain {
		const format: FormatOptions = { fontSize: '10px', color };
		return this.add(nameOrValue, value, format);
	}

	// Group subsequent logs
	group(label?: string): LoggerChain {
		this.grouped = true;
		this.groupLabel = label;
		return this;
	}

	// Call this method to manually output the log message immediately
	output(): void {
		if (this.outputTimer) {
			clearTimeout(this.outputTimer);
		}

		if (isDebugEnabled()) {
			if (this.grouped && this.groupLabel) {
				console.group(this.groupLabel);
			} else if (this.grouped) {
				console.group();
			}

			this.formatAndOutput();

			if (this.grouped) {
				console.groupEnd();
			}
		}
	}

	private scheduleOutput(): void {
		this.outputTimer = setTimeout(() => {
			this.output();
		}, 0);
	}

	private formatAndOutput(): void {
		const icon = this.icon ? `${this.icon} ` : '';
		const prefixToUse = this.customPrefix || globalPrefix;
		const mainMessage = [`%c${prefixToUse}`, `color: ${this.defaultColor}`, `${icon}${this.message}`];

		// Output main message
		console.debug(...mainMessage);

		// Output fields
		for (const field of this.fields) {
			if (field.newLine) {
				// Output on new line
				const parts = this.formatField(field);
				console.debug(...parts);
			} else {
				// Output inline (same line style)
				const parts = this.formatField(field, true);
				console.debug(...parts);
			}
		}
	}

	private formatField(field: LogField, inline = false): unknown[] {
		const parts: unknown[] = [];
		const style = this.buildStyle(field.format);

		if (inline) {
			parts.push('  '); // Indent for inline fields
		} else {
			parts.push('    '); // More indent for new line fields
		}

		if (field.name) {
			parts.push(`${field.name}:`);
		}

		if (style) {
			parts.push(`%c${field.value}`, style);
		} else {
			parts.push(field.value);
		}

		return parts;
	}

	private buildStyle(format?: FormatOptions): string | undefined {
		if (!format) return undefined;

		const styles: string[] = [];

		if (format.color) styles.push(`color: ${format.color}`);
		if (format.backgroundColor) styles.push(`background-color: ${format.backgroundColor}`);
		if (format.fontWeight) styles.push(`font-weight: ${format.fontWeight}`);
		if (format.fontStyle) styles.push(`font-style: ${format.fontStyle}`);
		if (format.textDecoration) styles.push(`text-decoration: ${format.textDecoration}`);
		if (format.fontSize) styles.push(`font-size: ${format.fontSize}`);

		return styles.length > 0 ? styles.join('; ') : undefined;
	}
}

export class Logger {
	log(message: string): LoggerChain {
		return new LoggerChain(message, '#00a67c');
	}

	info(message: string): LoggerChain {
		return new LoggerChain(message, '#007bff', '🔍');
	}

	warn(message: string): LoggerChain {
		return new LoggerChain(message, '#ffc107', '⚠️');
	}

	errLog(message: string): LoggerChain {
		return new LoggerChain(message, '#dc3545', '❌');
	}

	success(message: string): LoggerChain {
		return new LoggerChain(message, '#28a745', '✅');
	}
}

export const logger = new Logger();

function isDebugEnabled(): boolean {
	return userUtils.debugMode();
}

function formatMessage(prefix: string, color: string, args: unknown[]) {
	return [`%c${prefix}`, `color: ${color}`, ...args];
}
